import React, {useState, useEffect} from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import {FirebaseStateContext} from "../firebase/firebaseContext";
import {validateCourseLead} from "./courseValidation";
import {submitCourseLead} from "./courseLeadService";

export const CourseInterestModal = ({
    open,
    onClose,
    instructor,
    selectedCourse = null,
}) => {
    const firebaseState = React.useContext(FirebaseStateContext);
    const db = firebaseState?.db || null;

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [courseId, setCourseId] = useState("");
    const [message, setMessage] = useState("");
    const [hasConsent, setHasConsent] = useState(false);

    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const upcomingCourses = React.useMemo(() => {
        return Array.isArray(instructor?.upcomingCourses)
            ? instructor.upcomingCourses.filter((c) => c && c.status !== "completed")
            : [];
    }, [instructor?.upcomingCourses]);

    useEffect(() => {
        if (open) {
            setName("");
            setEmail("");
            setPhone("");
            setMessage("");
            setHasConsent(false);
            setFormErrors({});
            setIsSuccess(false);
            setSubmitError(null);

            if (selectedCourse && selectedCourse.id) {
                setCourseId(selectedCourse.id);
            } else if (upcomingCourses.length > 0) {
                setCourseId(upcomingCourses[0].id);
            } else {
                setCourseId("");
            }
        }
    }, [open, selectedCourse, upcomingCourses]);

    if (!instructor) return null;

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        const selectedCourseObj = upcomingCourses.find((c) => c.id === courseId);
        const leadPayload = {
            name,
            email,
            phone,
            courseId: courseId || null,
            courseTitle: selectedCourseObj ? selectedCourseObj.title : "Generell henvendelse",
            message,
            hasConsent,
        };

        const validation = validateCourseLead(leadPayload);
        if (!validation.isValid) {
            setFormErrors(validation.errors);
            return;
        }

        setIsSubmitting(true);
        setSubmitError(null);

        try {
            await submitCourseLead(db, instructor, leadPayload);
            setIsSuccess(true);
        } catch (err) {
            setSubmitError(err.message || "Kunne ikke sende henvendelsen. Vennligst prøv igjen.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={isSubmitting ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            aria-labelledby="course-interest-dialog-title"
        >
            <DialogTitle
                id="course-interest-dialog-title"
                component="div"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pb: 1,
                    pr: 1.5,
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                    <Box
                        sx={{
                            display: "inline-flex",
                            p: 0.75,
                            borderRadius: 2,
                            bgcolor: "rgba(40, 82, 56, 0.08)",
                            color: "primary.main",
                        }}
                    >
                        <ContactMailIcon fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="h6" component="h2" sx={{fontWeight: 700, color: "primary.main", fontSize: "1.1rem"}}>
                            Meld interesse for kurs
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            hos {instructor.name} {instructor.place ? `(${instructor.place})` : ""}
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    aria-label="Lukk"
                    onClick={onClose}
                    disabled={isSubmitting}
                    size="small"
                    sx={{
                        color: "text.secondary",
                        minWidth: 44,
                        minHeight: 44,
                        "&:focus-visible": {
                            outline: "2px solid #285238",
                            outlineOffset: "2px",
                        },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{p: {xs: 2, sm: 3}}}>
                {isSuccess ? (
                    <Box sx={{textAlign: "center", py: 4}}>
                        <CheckCircleOutlineIcon sx={{fontSize: 64, color: "success.main", mb: 1.5}} />
                        <Typography variant="h5" sx={{fontWeight: 700, color: "primary.main", mb: 1}}>
                            Takk for interessen!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{maxWidth: 420, mx: "auto", mb: 3, lineHeight: 1.6}}>
                            Henvendelsen din er nå sendt til {instructor.name}. Kursholderen vil kontakte deg på e-post eller telefon med nærmere informasjon om oppstart og ledige plasser.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onClose}
                            sx={{
                                fontWeight: 700,
                                textTransform: "none",
                                minWidth: 140,
                                minHeight: 44,
                                borderRadius: 2,
                                "&:focus-visible": {
                                    outline: "2px solid #285238",
                                    outlineOffset: "2px",
                                },
                            }}
                        >
                            Lukk
                        </Button>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        {/* Privacy notice banner */}
                        <Alert
                            severity="info"
                            icon={<LockOutlinedIcon fontSize="inherit" />}
                            sx={{
                                mb: 2.5,
                                fontSize: "0.85rem",
                                bgcolor: "rgba(40, 82, 56, 0.05)",
                                color: "text.primary",
                                borderColor: "rgba(40, 82, 56, 0.15)",
                                border: "1px solid",
                                "& .MuiAlert-icon": {color: "primary.main"},
                            }}
                        >
                            <Typography variant="caption" component="div" sx={{fontWeight: 700, mb: 0.25}}>
                                Personvern og konfidensialitet (GDPR)
                            </Typography>
                            Oppgi kun praktiske opplysninger for kontakt. Av hensyn til ditt personvern ber vi om at du <strong>ikke</strong> skriver helseopplysninger eller diagnoser her.
                        </Alert>

                        {submitError && (
                            <Alert severity="error" sx={{mb: 2.5}}>
                                {submitError}
                            </Alert>
                        )}

                        <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                            <TextField
                                label="Fullt navn"
                                placeholder="f.eks. Ola Nordmann"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    if (formErrors.name) setFormErrors({...formErrors, name: null});
                                }}
                                error={Boolean(formErrors.name)}
                                helperText={formErrors.name}
                                required
                                fullWidth
                                size="small"
                            />

                            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2}}>
                                <TextField
                                    label="E-postadresse"
                                    type="email"
                                    placeholder="ola@eksempel.no"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (formErrors.email) setFormErrors({...formErrors, email: null});
                                    }}
                                    error={Boolean(formErrors.email)}
                                    helperText={formErrors.email}
                                    required
                                    fullWidth
                                    size="small"
                                />

                                <TextField
                                    label="Telefonnummer (valgfritt)"
                                    type="tel"
                                    placeholder="f.eks. 98 76 54 32"
                                    value={phone}
                                    onChange={(e) => {
                                        setPhone(e.target.value);
                                        if (formErrors.phone) setFormErrors({...formErrors, phone: null});
                                    }}
                                    error={Boolean(formErrors.phone)}
                                    helperText={formErrors.phone}
                                    fullWidth
                                    size="small"
                                />
                            </Box>

                            {upcomingCourses.length > 0 && (
                                <TextField
                                    select
                                    label="Velg kurskull du er interessert i"
                                    value={courseId}
                                    onChange={(e) => setCourseId(e.target.value)}
                                    fullWidth
                                    size="small"
                                >
                                    {upcomingCourses.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.title} ({c.startDate ? `Oppstart ${c.startDate}` : c.location || "Planlagt"})
                                        </MenuItem>
                                    ))}
                                    <MenuItem value="">Generell henvendelse / annet tidspunkt</MenuItem>
                                </TextField>
                            )}

                            <TextField
                                label="Kort melding eller spørsmål (valgfritt)"
                                placeholder="f.eks. Passer best på kveldstid, eller har spørsmål om kurslokalet."
                                multiline
                                rows={3}
                                value={message}
                                onChange={(e) => {
                                    setMessage(e.target.value);
                                    if (formErrors.message) setFormErrors({...formErrors, message: null});
                                }}
                                error={Boolean(formErrors.message)}
                                helperText={formErrors.message || "Maks 500 tegn. Vennligst unngå sensitive helsedetaljer."}
                                fullWidth
                                size="small"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={hasConsent}
                                        onChange={(e) => {
                                            setHasConsent(e.target.checked);
                                            if (formErrors.hasConsent) setFormErrors({...formErrors, hasConsent: null});
                                        }}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{color: formErrors.hasConsent ? "error.main" : "text.secondary"}}>
                                        Jeg samtykker til at kursholderen kan kontakte meg på e-post eller telefon vedrørende kurset.
                                    </Typography>
                                }
                                sx={{mt: 0.5}}
                            />
                            {formErrors.hasConsent && (
                                <Typography variant="caption" color="error" sx={{ml: 4, mt: -1}}>
                                    {formErrors.hasConsent}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            {!isSuccess && (
                <DialogActions sx={{px: 3, py: 2}}>
                    <Button
                        onClick={onClose}
                        disabled={isSubmitting}
                        sx={{
                            textTransform: "none",
                            minHeight: 44,
                            fontWeight: 600,
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        Avbryt
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{
                            fontWeight: 700,
                            textTransform: "none",
                            minWidth: 140,
                            minHeight: 44,
                            borderRadius: 2,
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Send henvendelse"}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};
